import React, { ButtonHTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router-dom';

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type ButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'ghost';

interface CommonButtonProps {
  size?: ButtonSize;
  variant?: ButtonVariant;
  fullWidth?: boolean;
  className?: string;
  disabled?: boolean;
  icon?: ReactNode;
  isLoading?: boolean;
}

interface ButtonAsButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'>, CommonButtonProps {
  as?: 'button';
  to?: never;
  children: ReactNode;
}

interface ButtonAsLinkProps extends CommonButtonProps {
  as: 'link';
  to: string;
  onClick?: () => void;
  children: ReactNode;
}

type ButtonProps = ButtonAsButtonProps | ButtonAsLinkProps;

const Button: React.FC<ButtonProps> = ({
  children,
  size = 'md',
  variant = 'primary',
  fullWidth = false,
  className = '',
  disabled = false,
  icon,
  isLoading = false,
  ...props
}) => {
  const getSizeClasses = (): string => {
    switch (size) {
      case 'xs':
        return 'px-2 py-1 text-xs';
      case 'sm':
        return 'px-2.5 py-1.5 text-sm';
      case 'md':
        return 'px-3.5 py-2 text-sm';
      case 'lg':
        return 'px-4 py-2 text-base';
      case 'xl':
        return 'px-6 py-3 text-base';
      default:
        return 'px-3.5 py-2 text-sm';
    }
  };

  const getVariantClasses = (): string => {
    switch (variant) {
      case 'primary':
        return 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm focus-visible:outline-indigo-600';
      case 'secondary':
        return 'bg-white hover:bg-gray-50 text-gray-900 focus-visible:outline-gray-600';
      case 'success':
        return 'bg-green-600 hover:bg-green-700 text-white shadow-sm focus-visible:outline-green-600';
      case 'danger':
        return 'bg-red-600 hover:bg-red-700 text-white shadow-sm focus-visible:outline-red-600';
      case 'warning':
        return 'bg-yellow-500 hover:bg-yellow-600 text-white shadow-sm focus-visible:outline-yellow-600';
      case 'info':
        return 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm focus-visible:outline-blue-600';
      case 'ghost':
        return 'bg-transparent hover:bg-gray-100 text-gray-700 focus-visible:outline-gray-600';
      default:
        return 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm focus-visible:outline-indigo-600';
    }
  };

  const baseClasses = `
    inline-flex items-center justify-center font-semibold
    rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2
    transition-colors duration-200 ease-in-out
    ${getVariantClasses()}
    ${getSizeClasses()}
    ${fullWidth ? 'w-full' : ''}
    ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
    ${className}
  `;

  const content = (
    <>
      {isLoading && (
        <svg
          className={`animate-spin -ml-1 mr-2 h-4 w-4 ${variant === 'secondary' || variant === 'ghost' ? 'text-gray-700' : 'text-white'}`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
      )}
      {icon && <span className={`${children ? 'mr-2' : ''}`}>{icon}</span>}
      {children}
    </>
  );

  if (props.as === 'link') {
    const { as, to, ...restProps } = props;
    return (
      <Link to={to} className={baseClasses} {...restProps}>
        {content}
      </Link>
    );
  } else {
    const { as, ...restProps } = props;
    return (
      <button type="button" className={baseClasses} disabled={disabled || isLoading} {...restProps}>
        {content}
      </button>
    );
  }
};

export default Button; 